import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { graphqlClient } from "./graphql";
import { graphql } from "@/__generated__";
import { FlashcardFieldGroupEnum, type FlashcardDeckLevelEnum } from "@/__generated__/graphql";

// ---------- Types ----------

// Server enum (BEGINNER / INTERMEDIATE / FLUENT) — an unknown value is refused at
// GraphQL validation, so the Settings select can never 500 the mutation.
export type DeckLevel = FlashcardDeckLevelEnum;
// Upper bound on the per-day caps; mirrors FlashcardDeck::MAX_DAILY_CAP (the
// server is the authority — this only lets the inputs refuse early).
export const MAX_DAILY_CAP = 500;
export type FlashcardState = "fresh" | "learning" | "review" | "relearning";
export type Rating = "again" | "hard" | "good" | "easy";

export interface FlashcardDeck {
  id: string;
  displayName: string;
  level: DeckLevel;
  dailyNewCap: number;
  dailyReviewCap: number;
  desiredRetention: number;
  learnStepsMinutes: number[];
  relearnStepsMinutes: number[];
  mergedIntoDeckId: string | null;
  merged: boolean;
  personal: boolean;
  dueCount: number;
  newCount: number;
  learningCount: number;
  totalCount: number;
  targetLanguage: { id: string; code: string; name: string };
  tutorProfile: { id: string; user: { firstName: string } } | null;
  mergedSourceDecks: AbsorbedFlashcardDeck[];
}

// A deck merged into another: listed under its target so it can be unmerged.
export interface AbsorbedFlashcardDeck {
  id: string;
  personal: boolean;
  totalCount: number;
  targetLanguage: { id: string; code: string; name: string };
  tutorProfile: { id: string; user: { firstName: string } } | null;
}

export interface FlashcardCardType {
  id: string;
  name: string;
  position: number;
  frontFields: string[];
  backFields: string[];
}

export interface Flashcard {
  id: string;
  state: FlashcardState;
  nextDueAt: string;
  reviewCount: number;
  hideFromReview: boolean;
  targetWord: string;
  translation: string;
  pronunciationWord: string | null;
  exampleSentenceL2: string;
  exampleSentenceL1: string;
  pronunciationSentence: string | null;
  pictureUrl: string | null;
  wordAudioUrl: string | null;
  sentenceAudioUrl: string | null;
  cardType: FlashcardCardType;
  deck: { id: string; displayName: string };
  tutorTag: string | null;
  suspended?: boolean;
  leech?: boolean;
  // Latest field regeneration (RegenerateFlashcardFieldJob): "pending" while it
  // runs — the browse query polls until it changes — and "failed" with a stable
  // error code (see regenErrorKey) when the last attempt did not go through.
  regenerationStatus?: "none" | "pending" | "failed";
  regenerationErrorCode?: string | null;
  // The text the student actually captured, when the AI created the card with
  // a corrected spelling (#825 phase 3). Denormalised onto the card, so it
  // outlives the vocabulary entry; null on every other card.
  originalCaptureText?: string | null;
  // The capture this card came from — Browse's "Keep my spelling" needs it.
  // Null once the entry is gone (the chip stays, the action disappears).
  vocabularyEntryId?: string | null;
  // Next-interval preview in SECONDS per rating (sub-day values are learning
  // steps) — present only on the scheduled review queue, not on
  // custom-study/practice cards.
  intervalPreviews?: { again: number; hard: number; good: number; easy: number } | null;
}

// Session metadata returned alongside the study queue — drives the
// end-of-session screen (what was capped out today, when the next learning
// step comes back).
export interface StudyQueueMeta {
  learningCount: number;
  reviewCount: number;
  newCount: number;
  reviewBacklog: number;
  newBacklog: number;
  nextLearningDueInSeconds: number | null;
}

// ---------- Queries ----------

const DECKS_QUERY = graphql(`
  query MyFlashcardDecks {
    myFlashcardDecks {
      id
      displayName
      level
      dailyNewCap
      dailyReviewCap
      desiredRetention
      learnStepsMinutes
      relearnStepsMinutes
      mergedIntoDeckId
      merged
      personal
      dueCount
      newCount
      learningCount
      totalCount
      targetLanguage { id code name }
      tutorProfile { id user { firstName } }
      mergedSourceDecks {
        id
        personal
        totalCount
        targetLanguage { id code name }
        tutorProfile { id user { firstName } }
      }
    }
  }
`);

const TO_STUDY_COUNT_QUERY = graphql(`
  query FlashcardsToStudyCount {
    flashcardsToStudyCount
  }
`);

const CARD_TYPES_QUERY = graphql(`
  query MyFlashcardCardTypes {
    myFlashcardCardTypes {
      id
      name
      position
      frontFields
      backFields
    }
  }
`);

// Layout presets for the Settings "Add card type" picker. The server owns the
// layouts (FlashcardCardType::PRESETS); `key` is a stable code translated via
// PRESET_LABEL_KEYS.
const CARD_TYPE_PRESETS_QUERY = graphql(`
  query FlashcardCardTypePresets {
    flashcardCardTypePresets {
      key
      frontFields
      backFields
    }
  }
`);

// Exported so a vitest can assert the interval-preview field is selected on the
// scheduled-review queue (and ONLY there) — the field's correct placement is not
// otherwise enforced by tsc, since intervalPreviews is optional on Flashcard.
export const DUE_QUERY = graphql(`
  query DueFlashcards($deckId: ID!, $limit: Int) {
    dueFlashcards(deckId: $deckId, limit: $limit) {
      id
      state
      nextDueAt
      reviewCount
      hideFromReview
      targetWord
      translation
      pronunciationWord
      exampleSentenceL2
      exampleSentenceL1
      pronunciationSentence
      pictureUrl
      wordAudioUrl
      sentenceAudioUrl
      cardType { id name frontFields backFields }
      deck { id displayName }
      tutorTag
      originalCaptureText
      intervalPreviews { again hard good easy }
    }
  }
`);

// The scheduled-review session payload: the capped, interleaved, sibling-spaced
// queue plus end-of-session metadata. Exported for the vitest that guards the
// intervalPreviews selection placement.
export const STUDY_QUEUE_QUERY = graphql(`
  query FlashcardStudyQueue($deckId: ID!) {
    flashcardStudyQueue(deckId: $deckId) {
      cards {
        id
        state
        nextDueAt
        reviewCount
        hideFromReview
        targetWord
        translation
        pronunciationWord
        exampleSentenceL2
        exampleSentenceL1
        pronunciationSentence
        pictureUrl
        wordAudioUrl
        sentenceAudioUrl
        cardType { id name frontFields backFields }
        deck { id displayName }
        tutorTag
        originalCaptureText
        intervalPreviews { again hard good easy }
      }
      learningCount
      reviewCount
      newCount
      reviewBacklog
      newBacklog
      nextLearningDueInSeconds
    }
  }
`);

// Custom-study modes. cram/mistakes are pure practice: ratings are NEVER
// submitted, so they can't touch the FSRS schedule. "ahead" (Anki's review
// ahead — future-due cards) and "new" (study new cards early — fresh cards
// introduced ahead of the daily cap) are REAL sessions whose ratings submit
// through SubmitFlashcardReviews and schedule the cards.
export type StudyMode = "cram" | "mistakes" | "new" | "ahead";

// Review-ahead queue: future-due cards, soonest first. Selects intervalPreviews
// because ahead ratings genuinely reschedule (unlike CUSTOM_STUDY_QUERY below).
export const STUDY_AHEAD_QUERY = graphql(`
  query StudyAheadFlashcards($deckId: ID!, $limit: Int) {
    customStudyFlashcards(deckId: $deckId, mode: "ahead", limit: $limit) {
      id
      state
      nextDueAt
      reviewCount
      hideFromReview
      targetWord
      translation
      pronunciationWord
      exampleSentenceL2
      exampleSentenceL1
      pronunciationSentence
      pictureUrl
      wordAudioUrl
      sentenceAudioUrl
      cardType { id name frontFields backFields }
      deck { id displayName }
      tutorTag
      originalCaptureText
      intervalPreviews { again hard good easy }
    }
  }
`);

// Study-new queue: never-seen cards, oldest first, beyond today's new cap.
// Selects intervalPreviews because these ratings genuinely introduce the card
// (9.1-D) — before, "new" was practice-only and the cards came back as fresh.
export const STUDY_NEW_QUERY = graphql(`
  query StudyNewFlashcards($deckId: ID!, $limit: Int) {
    customStudyFlashcards(deckId: $deckId, mode: "new", limit: $limit) {
      id
      state
      nextDueAt
      reviewCount
      hideFromReview
      targetWord
      translation
      pronunciationWord
      exampleSentenceL2
      exampleSentenceL1
      pronunciationSentence
      pictureUrl
      wordAudioUrl
      sentenceAudioUrl
      cardType { id name frontFields backFields }
      deck { id displayName }
      tutorTag
      originalCaptureText
      intervalPreviews { again hard good easy }
    }
  }
`);

// Pure-practice queue (cram/mistakes): no intervalPreviews — ratings never
// reschedule, so the backend shouldn't run FSRS four times per card for a
// preview the client would discard.
export const CUSTOM_STUDY_QUERY = graphql(`
  query CustomStudyFlashcards($deckId: ID!, $mode: String!, $limit: Int) {
    customStudyFlashcards(deckId: $deckId, mode: $mode, limit: $limit) {
      id
      state
      nextDueAt
      reviewCount
      hideFromReview
      targetWord
      translation
      pronunciationWord
      exampleSentenceL2
      exampleSentenceL1
      pronunciationSentence
      pictureUrl
      wordAudioUrl
      sentenceAudioUrl
      cardType { id name frontFields backFields }
      deck { id displayName }
      tutorTag
      originalCaptureText
    }
  }
`);

const BROWSE_QUERY = graphql(`
  query BrowseFlashcards($deckId: ID!, $lessonId: ID, $search: String, $mastery: String, $tutorTag: String, $sort: String, $limit: Int, $offset: Int) {
    browseFlashcards(deckId: $deckId, lessonId: $lessonId, search: $search, mastery: $mastery, tutorTag: $tutorTag, sort: $sort, limit: $limit, offset: $offset) {
      id
      state
      nextDueAt
      reviewCount
      suspended
      leech
      targetWord
      translation
      exampleSentenceL2
      exampleSentenceL1
      pictureUrl
      tutorTag
      regenerationStatus
      regenerationErrorCode
      originalCaptureText
      vocabularyEntryId
      deck { id displayName }
    }
  }
`);

// ---------- Mutations ----------

const SUBMIT_REVIEWS_MUTATION = graphql(`
  mutation Submit($input: SubmitFlashcardReviewsInput!) {
    submitFlashcardReviews(input: $input) {
      flashcards {
        id
        state
        nextDueAt
        reviewCount
        intervalPreviews { again hard good easy }
      }
      errorCodes
      errors
    }
  }
`);

// Stable codes SubmitFlashcardReviews answers a refused rating with. Anything
// else — or a transport failure — reads as the generic line; the server's own
// sentence (which carries a card UUID) is never shown.
export const REVIEW_ERROR_CODES = ["already_answered", "flashcard_not_found", "not_authenticated"] as const;

export function reviewErrorKey(code: string | null | undefined): string {
  return (REVIEW_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? `flashcards.reviewErrors.${code}`
    : "flashcards.reviewErrors.generic";
}

const UNDO_REVIEW_MUTATION = graphql(`
  mutation UndoReview($input: UndoFlashcardReviewInput!) {
    undoFlashcardReview(input: $input) {
      flashcard {
        id
        state
        nextDueAt
        reviewCount
        intervalPreviews { again hard good easy }
      }
      errors
    }
  }
`);

const SET_SUSPENDED_MUTATION = graphql(`
  mutation SetSuspended($input: SetFlashcardSuspendedInput!) {
    setFlashcardSuspended(input: $input) {
      flashcard { id suspended }
      errorCode
      errors
    }
  }
`);

const REGENERATE_MUTATION = graphql(`
  mutation Regenerate($input: RegenerateFlashcardFieldInput!) {
    regenerateFlashcardField(input: $input) {
      flashcard { id regenerationStatus }
      pending
      rateLimited
      errors
    }
  }
`);

const FLAG_MUTATION = graphql(`
  mutation Flag($input: FlagFlashcardInput!) {
    flagFlashcard(input: $input) {
      flashcardFlag { id hideFromReview }
      alreadyFlagged
      errorCode
      errors
    }
  }
`);

const MERGE_MUTATION = graphql(`
  mutation Merge($input: MergeFlashcardDecksInput!) {
    mergeFlashcardDecks(input: $input) {
      sourceDeck { id mergedIntoDeckId }
      errors
    }
  }
`);

const UNMERGE_MUTATION = graphql(`
  mutation Unmerge($input: UnmergeFlashcardDeckInput!) {
    unmergeFlashcardDeck(input: $input) {
      deck { id mergedIntoDeckId }
      errors
    }
  }
`);

const UPDATE_DECK_SETTINGS_MUTATION = graphql(`
  mutation UpdateDeck($input: UpdateFlashcardDeckSettingsInput!) {
    updateFlashcardDeckSettings(input: $input) {
      deck { id level dailyNewCap dailyReviewCap desiredRetention learnStepsMinutes relearnStepsMinutes }
      errors
    }
  }
`);

const UPDATE_CARD_TYPES_MUTATION = graphql(`
  mutation UpdateCardTypes($input: UpdateCardTypesInput!) {
    updateCardTypes(input: $input) {
      cardTypes { id name position frontFields backFields }
      errorCode
      errorDetail
      errors
    }
  }
`);

const UPDATE_UI_LOCALE_MUTATION = graphql(`
  mutation UpdateUiLocale($input: UpdateUiLocaleInput!) {
    updateUiLocale(input: $input) {
      user { id uiLocale }
      errors
    }
  }
`);

// ---------- Hooks ----------

export function useFlashcardDecks() {
  return useQuery({
    queryKey: ["flashcards", "decks"],
    queryFn: async () => {
      const data = await graphqlClient.request(DECKS_QUERY);
      return data.myFlashcardDecks;
    },
  });
}

// One number for the nav badge: what a study session would serve right now.
// Its own slim query, not a sum over DECKS_QUERY — the nav renders on every
// page and that document carries names, settings and merge trees the badge has
// no use for. Under the ["flashcards"] prefix so every existing
// invalidateQueries({ queryKey: ["flashcards"] }) already refreshes it: a
// capture, a review, an undo and a deck merge all change this number.
export function useFlashcardsToStudyCount(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["flashcards", "toStudyCount"],
    queryFn: async (): Promise<number> => {
      const data = await graphqlClient.request(TO_STUDY_COUNT_QUERY);
      return data.flashcardsToStudyCount;
    },
    enabled: opts.enabled ?? true,
    // The count only moves on the student's own actions, which invalidate it.
    staleTime: 60_000,
  });
}

export function useFlashcardCardTypes() {
  return useQuery({
    queryKey: ["flashcards", "cardTypes"],
    queryFn: async () => {
      const data = await graphqlClient.request(CARD_TYPES_QUERY);
      return data.myFlashcardCardTypes;
    },
  });
}

export function useFlashcardCardTypePresets() {
  return useQuery({
    queryKey: ["flashcards", "cardTypePresets"],
    queryFn: async () => {
      const data = await graphqlClient.request(CARD_TYPE_PRESETS_QUERY);
      return data.flashcardCardTypePresets;
    },
    // Static product config — never changes within a session.
    staleTime: Infinity,
  });
}

export function useDueFlashcards(deckId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["flashcards", "due", deckId, limit],
    queryFn: async () => {
      if (!deckId) return [];
      const data = await graphqlClient.request(DUE_QUERY, { deckId, limit });
      return data.dueFlashcards as Flashcard[];
    },
    enabled: !!deckId,
  });
}

export function useStudyQueue(deckId: string | null) {
  return useQuery({
    queryKey: ["flashcards", "studyQueue", deckId],
    queryFn: async () => {
      if (!deckId) return null;
      const data = await graphqlClient.request(STUDY_QUEUE_QUERY, { deckId });
      const queue = data.flashcardStudyQueue;
      if (!queue) return null;
      return {
        cards: queue.cards as Flashcard[],
        meta: {
          learningCount: queue.learningCount,
          reviewCount: queue.reviewCount,
          newCount: queue.newCount,
          reviewBacklog: queue.reviewBacklog,
          newBacklog: queue.newBacklog,
          nextLearningDueInSeconds: queue.nextLearningDueInSeconds ?? null,
        } satisfies StudyQueueMeta,
      };
    },
    enabled: !!deckId,
    // Each session start should see the freshest caps/queue, not a snapshot
    // from a previous visit.
    staleTime: 0,
  });
}

export function useCustomStudyFlashcards(deckId: string | null, mode: StudyMode | null, limit = 50) {
  return useQuery({
    queryKey: ["flashcards", "customStudy", deckId, mode, limit],
    queryFn: async () => {
      if (!deckId || !mode) return [];
      const data = await graphqlClient.request(CUSTOM_STUDY_QUERY, { deckId, mode, limit });
      return data.customStudyFlashcards as Flashcard[];
    },
    enabled: !!deckId && !!mode,
    // Always refetch on mount so each "Study again" gives a fresh (re-randomized
    // for cram) queue rather than a stale snapshot from a previous session.
    staleTime: 0,
  });
}

export function useStudyAheadFlashcards(deckId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["flashcards", "studyAhead", deckId, limit],
    queryFn: async () => {
      if (!deckId) return [];
      const data = await graphqlClient.request(STUDY_AHEAD_QUERY, { deckId, limit });
      return data.customStudyFlashcards as Flashcard[];
    },
    enabled: !!deckId,
    staleTime: 0,
  });
}

export function useStudyNewFlashcards(deckId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["flashcards", "studyNew", deckId, limit],
    queryFn: async () => {
      if (!deckId) return [];
      const data = await graphqlClient.request(STUDY_NEW_QUERY, { deckId, limit });
      return data.customStudyFlashcards as Flashcard[];
    },
    enabled: !!deckId,
    staleTime: 0,
  });
}

export function useBrowseFlashcards(args: {
  deckId: string | null;
  // Only the cards captured in this lesson (vocabulary_entry.lesson_id) — the
  // post-lesson band's "See the cards" link. Part of the query key like every
  // other filter.
  lessonId?: string;
  search?: string;
  mastery?: string;
  tutorTag?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ["flashcards", "browse", args],
    queryFn: async () => {
      if (!args.deckId) return [];
      // deckId is narrowed to string by the guard above; spread it back in so the
      // required ID! variable type is satisfied.
      const data = await graphqlClient.request(BROWSE_QUERY, { ...args, deckId: args.deckId });
      return data.browseFlashcards as Flashcard[];
    },
    enabled: !!args.deckId,
    // A new filter (a search, a sort) keeps the current list on screen until
    // its own rows arrive; the page used to empty between the two.
    placeholderData: keepPreviousData,
    // A field regeneration runs in a background job; poll while any listed
    // card is still pending so the new picture/sentence shows up on its own.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.regenerationStatus === "pending") ? 2000 : false,
  });
}

// Stable failure codes a card's regeneration can end with — written by
// RegenerateFlashcardFieldJob, or "interrupted" for a reservation whose
// worker died (reported by the resolver once it is stale, then made durable
// by the hourly sweeper). Each is translated under
// flashcardsBrowse.regenError.* (anything unknown reads as internal_error
// rather than a raw code).
export const REGEN_ERROR_CODES = ["ai_unavailable", "example_invalid", "internal_error", "interrupted"] as const;

export function regenErrorKey(code: string | null | undefined): string {
  const known = (REGEN_ERROR_CODES as readonly string[]).includes(code ?? "") ? code : "internal_error";
  return `flashcardsBrowse.regenError.${known}`;
}

const CREATE_FLASHCARD_MUTATION = graphql(`
  mutation CreateFlashcard($input: CreateFlashcardInput!) {
    createFlashcard(input: $input) {
      createdCount
      errorCode
      errors
    }
  }
`);

export function useCreateFlashcard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      deckId: string;
      targetWord: string;
      translation: string;
      exampleSentenceL2?: string;
      exampleSentenceL1?: string;
      pronunciationWord?: string;
      pronunciationSentence?: string;
    }) =>
      graphqlClient.request(
        CREATE_FLASHCARD_MUTATION,
        { input }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flashcards"] }),
  });
}

const COMPLETE_FIELDS_MUTATION = graphql(`
  mutation CompleteFlashcardFields($input: CompleteFlashcardFieldsInput!) {
    completeFlashcardFields(input: $input) {
      translation
      pronunciationWord
      exampleSentenceL2
      exampleSentenceL1
      pronunciationSentence
      errorCode
      errors
    }
  }
`);

// AI assist for the Add Cards dialog: send the word + whatever is typed, get
// back values ONLY for the blank fields (typed fields return null — the
// server guarantees student input is never overwritten).
export function useCompleteFlashcardFields() {
  return useMutation({
    mutationFn: (input: {
      deckId: string;
      targetWord: string;
      translation?: string;
      pronunciationWord?: string;
      exampleSentenceL2?: string;
      exampleSentenceL1?: string;
      pronunciationSentence?: string;
    }) => graphqlClient.request(COMPLETE_FIELDS_MUTATION, { input }),
  });
}

const CREATE_BATCH_MUTATION = graphql(`
  mutation CreateFlashcardBatch($input: CreateFlashcardBatchInput!) {
    createFlashcardBatch(input: $input) {
      batch {
        id
        status
      }
      errorCode
      errors
    }
  }
`);

// Pasted-list batch: rows are created in the background; poll
// useFlashcardBatchImport with the returned batch id for progress.
export function useCreateFlashcardBatch() {
  return useMutation({
    mutationFn: (input: {
      deckId: string;
      rows: {
        targetWord: string;
        translation?: string;
        pronunciationWord?: string;
        exampleSentenceL2?: string;
        exampleSentenceL1?: string;
        pronunciationSentence?: string;
      }[];
      autoComplete?: boolean;
      skipDuplicates?: boolean;
    }) => graphqlClient.request(CREATE_BATCH_MUTATION, { input }),
  });
}

const BATCH_IMPORT_QUERY = graphql(`
  query FlashcardBatchImport($id: ID!) {
    flashcardBatchImport(id: $id) {
      id
      status
      totalCount
      pendingCount
      createdCount
      skippedCount
      failedCount
      rows {
        position
        targetWord
        status
        error
        errorCode
      }
    }
  }
`);

export type FlashcardBatchProgress = NonNullable<
  ResultOf<typeof BATCH_IMPORT_QUERY>["flashcardBatchImport"]
>;

// Progress of a background batch (pasted list or file import). Polls while
// the batch is processing, then stops on either terminal status
// ("completed" / "failed").
export function useFlashcardBatchImport(batchId: string | null) {
  return useQuery({
    queryKey: ["flashcards", "batchImport", batchId],
    queryFn: async () => {
      if (!batchId) return null;
      const data = await graphqlClient.request(BATCH_IMPORT_QUERY, { id: batchId });
      return data.flashcardBatchImport;
    },
    enabled: !!batchId,
    refetchInterval: (query) => (query.state.data?.status === "processing" ? 1500 : false),
  });
}

// Stable codes the batch machinery leaves on a row it failed (a per-row
// service error arrives as `error` text instead), each translated under
// flashcards.addDialog.batch.rowError.* — anything unknown reads as the
// generic row failure rather than a raw code.
export const BATCH_ROW_ERROR_CODES = ["ai_unavailable", "interrupted"] as const;

export function batchRowErrorKey(code: string): string {
  const known = (BATCH_ROW_ERROR_CODES as readonly string[]).includes(code) ? code : "unknown";
  return `flashcards.addDialog.batch.rowError.${known}`;
}

// Stable refusal codes retryFlashcardBatchImport can return in `errors`.
// Both are race-only (the Retry button is only offered on a failed batch,
// so another tab or retry got there first), each translated under
// flashcards.batchRetryError.*. Anything else — or a transport failure —
// has no key, and the caller falls back to its generic "couldn't retry".
export const BATCH_RETRY_ERROR_CODES = ["batch_still_processing", "nothing_to_retry"] as const;

export function batchRetryErrorKey(code: string | undefined): string | null {
  return (BATCH_RETRY_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? `flashcards.batchRetryError.${code}`
    : null;
}

// Stable refusal codes the three capture mutations (CaptureVocabulary,
// CaptureLessonVocabulary, SaveVocabularyEntry) can return in `errorCode`. The
// eight a real user can hit have their own copy under flashcards.captureErrors.*
// (the last three are the tutor-chat path of SaveVocabularyEntry — a support
// thread, a group conversation, or no lesson history with the student yet,
// #825 phase 2); the seven others only reach the UI through a stale page or a
// scripted call and share one neutral line. Anything else (or a transport
// failure) has no key, and the caller shows the server sentence / its generic
// copy.
export const CAPTURE_ERROR_CODES = [
  "deck_limit_reached",
  "not_authenticated",
  "not_a_student",
  "language_not_found",
  "lesson_not_found",
  "lesson_not_capturable",
  "message_not_found",
  "forbidden",
  "no_target_language",
  "empty_selection",
  "selection_too_long",
  "rate_limited",
  "daily_limit_reached",
  "lesson_limit_reached",
  "no_student_participant",
  "multiple_student_participants",
  "no_teaching_relationship",
] as const;

const CAPTURE_ERROR_KEYS: Record<(typeof CAPTURE_ERROR_CODES)[number], string> = {
  deck_limit_reached: "flashcards.captureErrors.deckLimitReached",
  not_authenticated: "flashcards.captureErrors.notAllowed",
  not_a_student: "flashcards.captureErrors.notAllowed",
  language_not_found: "flashcards.captureErrors.notAllowed",
  lesson_not_found: "flashcards.captureErrors.notAllowed",
  lesson_not_capturable: "flashcards.captureErrors.notAllowed",
  message_not_found: "flashcards.captureErrors.notAllowed",
  forbidden: "flashcards.captureErrors.notAllowed",
  no_target_language: "flashcards.captureErrors.notAllowed",
  empty_selection: "flashcards.captureErrors.emptySelection",
  selection_too_long: "flashcards.captureErrors.selectionTooLong",
  rate_limited: "flashcards.captureErrors.rateLimited",
  daily_limit_reached: "flashcards.captureErrors.dailyLimitReached",
  lesson_limit_reached: "flashcards.captureErrors.lessonLimitReached",
  no_student_participant: "flashcards.captureErrors.noStudentParticipant",
  multiple_student_participants: "flashcards.captureErrors.multipleStudentParticipants",
  no_teaching_relationship: "flashcards.captureErrors.noTeachingRelationship",
};

export function captureErrorKey(code: string | null | undefined): string | null {
  return (CAPTURE_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? CAPTURE_ERROR_KEYS[code as (typeof CAPTURE_ERROR_CODES)[number]]
    : null;
}

// Refusal codes → locale keys for the flashcard mutations whose English
// sentences used to be toasted raw in all 17 locales (review 2026-09-15).
// Each resolves to a generic line for a code this build does not know, so a
// refusal always reads in the student's language, never as the server's prose.
function keyOrGeneric(codes: readonly string[], code: string | null | undefined, prefix: string, generic: string): string {
  return codes.includes(code ?? "") ? `${prefix}.${code}` : generic;
}

export const CARD_TYPE_ERROR_CODES = ["too_many_card_types", "card_type_missing", "card_type_has_cards"] as const;
// card_type_has_cards takes the type's name (`errorDetail`) as {{name}}.
export function cardTypeErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(CARD_TYPE_ERROR_CODES, code, "flashcards.settings.cardTypeErrors", "flashcards.settings.cardTypesSaveFailed");
}

export const COMPLETE_FIELDS_ERROR_CODES = ["daily_limit_reached", "text_too_long", "word_required", "ai_unavailable"] as const;
export function completeFieldsErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(COMPLETE_FIELDS_ERROR_CODES, code, "flashcards.addDialog.completeErrors", "flashcards.addDialog.completeFailed");
}

export const CREATE_CARD_ERROR_CODES = ["fields_required", "word_too_long", "deck_not_found"] as const;
export function createCardErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(CREATE_CARD_ERROR_CODES, code, "flashcards.addDialog.createErrors", "flashcards.addDialog.createErrors.generic");
}

export const BATCH_START_ERROR_CODES = ["daily_limit_reached", "no_rows", "deck_not_found"] as const;
export function batchStartErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(BATCH_START_ERROR_CODES, code, "flashcards.addDialog.batch.startErrors", "flashcards.addDialog.batch.startFailed");
}

export const FLAG_ERROR_CODES = ["not_found", "needs_correction", "invalid_reason", "rate_limited"] as const;
export function flagErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(FLAG_ERROR_CODES, code, "flashcards.flag.errors", "flashcards.flag.errors.generic");
}

export const SUSPEND_ERROR_CODES = ["not_found"] as const;
export function suspendErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(SUSPEND_ERROR_CODES, code, "flashcardsBrowse.suspendErrors", "flashcardsBrowse.suspendErrors.generic");
}

// The upload endpoint (FlashcardImportsController) answers `{errors, code}`.
export const IMPORT_ERROR_CODES = [
  "no_file",
  "too_large",
  "unsupported_type",
  "deck_not_found",
  "daily_limit_reached",
  "unreadable",
  "not_anki",
  "no_rows",
] as const;
export function importErrorKey(code: string | null | undefined): string {
  return keyOrGeneric(IMPORT_ERROR_CODES, code, "flashcards.settings.importErrors", "flashcards.settings.importFailed");
}

const RETRY_BATCH_MUTATION = graphql(`
  mutation RetryFlashcardBatchImport($input: RetryFlashcardBatchImportInput!) {
    retryFlashcardBatchImport(input: $input) {
      batch { id status }
      errors
    }
  }
`);

// Re-queues a stopped batch's failed rows (no daily import slot is charged);
// the batch goes back to "processing" and useFlashcardBatchImport resumes
// polling it.
export function useRetryFlashcardBatchImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { batchId: string }) => graphqlClient.request(RETRY_BATCH_MUTATION, { input }),
    onSuccess: (_data, input) =>
      qc.invalidateQueries({ queryKey: ["flashcards", "batchImport", input.batchId] }),
  });
}

const WORD_DUPLICATE_QUERY = graphql(`
  query FlashcardWordDuplicate($deckId: ID!, $word: String!) {
    flashcardWordDuplicate(deckId: $deckId, word: $word)
  }
`);

// Advisory duplicate probe for the Add Cards dialog. Pass the word already
// debounced — every distinct (deck, word) pair is one query. Never blocks
// creation; the caller only shows a warning.
export function useFlashcardWordDuplicate(deckId: string | null, word: string) {
  const trimmed = word.trim();
  return useQuery({
    queryKey: ["flashcards", "wordDuplicate", deckId, trimmed.toLowerCase()],
    queryFn: async () => {
      if (!deckId || !trimmed) return false;
      const data = await graphqlClient.request(WORD_DUPLICATE_QUERY, { deckId, word: trimmed });
      return data.flashcardWordDuplicate;
    },
    enabled: !!deckId && !!trimmed,
  });
}

export function useSubmitReviews() {
  const qc = useQueryClient();
  return useMutation({
    // No reviewedAt: the server clock schedules every review (client clock skew
    // must not move due dates). expectedReviewCount lets the server reject a
    // duplicate (double-tap / retry / second tab) instead of double-applying.
    mutationFn: (reviews: { flashcardId: string; rating: Rating; expectedReviewCount?: number; durationMs?: number }[]) =>
      graphqlClient.request(
        SUBMIT_REVIEWS_MUTATION,
        { input: { reviews } }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flashcards"] });
    },
  });
}

export function useUndoReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { flashcardId: string; expectedReviewCount?: number }) =>
      graphqlClient.request(UNDO_REVIEW_MUTATION, { input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flashcards"] });
    },
  });
}

export function useSetFlashcardSuspended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { flashcardId: string; suspended: boolean }) =>
      graphqlClient.request(SET_SUSPENDED_MUTATION, { input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flashcards"] }),
  });
}

// Regeneration is asynchronous: the mutation reserves the student's daily slot
// and queues RegenerateFlashcardFieldJob, answering `pending`. The card is
// marked pending in the browse cache right away so the poll
// (useBrowseFlashcards) takes over without a "none" flicker in between.
export function useRegenerateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { flashcardId: string; fieldGroup: "picture" | "example_sentence" }) =>
      graphqlClient.request(REGENERATE_MUTATION, {
        input: {
          flashcardId: input.flashcardId,
          fieldGroup:
            input.fieldGroup === "picture"
              ? FlashcardFieldGroupEnum.Picture
              : FlashcardFieldGroupEnum.ExampleSentence,
        },
      }),
    onSuccess: (data) => {
      const r = data.regenerateFlashcardField;
      if (r.pending && r.flashcard) {
        const id = r.flashcard.id;
        qc.setQueriesData<Flashcard[]>({ queryKey: ["flashcards", "browse"] }, (old) =>
          old?.map((c) => (c.id === id ? { ...c, regenerationStatus: "pending" } : c))
        );
      }
      qc.invalidateQueries({ queryKey: ["flashcards"] });
    },
  });
}

export function useFlagFlashcard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { flashcardId: string; reason: string; details?: string; hideFromReview?: boolean }) =>
      graphqlClient.request(FLAG_MUTATION, { input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flashcards"] }),
  });
}

export function useMergeDecks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceDeckId: string; targetDeckId: string }) =>
      graphqlClient.request(MERGE_MUTATION, { input }),
    // Browse lists and the study queue span merged decks too — refresh them
    // all. The Puzzles page keeps its own deck list, which went on offering
    // an absorbed deck.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flashcards"] });
      qc.invalidateQueries({ queryKey: ["puzzles", "decks"] });
    },
  });
}

export function useUnmergeDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { deckId: string }) =>
      graphqlClient.request(UNMERGE_MUTATION, { input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flashcards"] });
      qc.invalidateQueries({ queryKey: ["puzzles", "decks"] });
    },
  });
}

export function useUpdateDeckSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      deckId: string;
      level?: DeckLevel;
      dailyNewCap?: number;
      dailyReviewCap?: number;
      desiredRetention?: number;
      learnStepsMinutes?: number[];
      relearnStepsMinutes?: number[];
    }) =>
      graphqlClient.request(
        UPDATE_DECK_SETTINGS_MUTATION,
        { input }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flashcards", "decks"] }),
  });
}

export function useUpdateCardTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardTypes: { id?: string; name: string; position: number; frontFields: string[]; backFields: string[] }[]) =>
      graphqlClient.request(
        UPDATE_CARD_TYPES_MUTATION,
        {
          // Strip any client-only fields (e.g. the drag-and-drop `uid`) — the
          // GraphQL input rejects unknown args, which would fail the whole save.
          input: {
            cardTypes: cardTypes.map((ct) => ({
              id: ct.id,
              name: ct.name,
              position: ct.position,
              frontFields: ct.frontFields,
              backFields: ct.backFields,
            })),
          },
        }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flashcards", "cardTypes"] }),
  });
}

export function useUpdateUiLocale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: string) =>
      graphqlClient.request(UPDATE_UI_LOCALE_MUTATION, { input: { locale } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// Upper bound on Personal + tutor decks per student; mirrors
// FlashcardDeck::MAX_DECKS_PER_STUDENT (the server refuses with
// deck_limit_reached — this only feeds the translated toast).
export const MAX_DECKS_PER_STUDENT = 20;

const CREATE_DECK_MUTATION = graphql(`
  mutation CreateFlashcardDeck($input: CreateFlashcardDeckInput!) {
    createFlashcardDeck(input: $input) {
      deck { id targetLanguage { id code name } }
      created
      errors
      errorCode
    }
  }
`);

// Stable refusal codes CreateFlashcardDeck can return in errorCode, each
// translated under flashcards.newDeck.errors.*; anything else (or a transport
// failure) has no key and the caller shows the server sentence / generic copy.
export const CREATE_DECK_ERROR_CODES = [
  "language_not_found",
  "signed_language_unsupported",
  "native_equals_target",
  // #878: the same collision, but the L1 it collides with was only guessed from
  // the interface locale. The student can fix that, so the copy asks.
  "needs_native_language",
  "deck_limit_reached",
] as const;

const CREATE_DECK_ERROR_KEYS: Record<(typeof CREATE_DECK_ERROR_CODES)[number], string> = {
  language_not_found: "flashcards.newDeck.errors.languageNotFound",
  signed_language_unsupported: "flashcards.newDeck.errors.signedUnsupported",
  native_equals_target: "flashcards.newDeck.errors.nativeEqualsTarget",
  needs_native_language: "flashcards.newDeck.errors.needsNativeLanguage",
  deck_limit_reached: "flashcards.newDeck.errors.limitReached",
};

export function createDeckErrorKey(code: string | null | undefined): string | null {
  return (CREATE_DECK_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? CREATE_DECK_ERROR_KEYS[code as (typeof CREATE_DECK_ERROR_CODES)[number]]
    : null;
}

// Creates (or finds — `created: false`) the student's Personal deck for a
// language. Both deck lists change: the puzzles page has its own decks query.
export function useCreateFlashcardDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { targetLanguageId: string; level?: DeckLevel }) =>
      graphqlClient.request(CREATE_DECK_MUTATION, { input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flashcards", "decks"] });
      qc.invalidateQueries({ queryKey: ["puzzles", "decks"] });
      // The first deck seeds the student's Default card type on the server.
      // Settings' card-type editor read a stale, empty list — and a Save from
      // it deleted the freshly seeded type.
      qc.invalidateQueries({ queryKey: ["flashcards", "cardTypes"] });
    },
  });
}

export const AVAILABLE_FIELDS = [
  "target_word",
  "translation",
  "example_sentence_l2",
  "example_sentence_l1",
  "word_audio",
  "sentence_audio",
  "picture",
  "pronunciation_word",
  "pronunciation_sentence",
] as const;

// i18n keys per card field — resolve with t() at display time (module-level
// English strings here would never re-render on language switch).
export const FIELD_LABEL_KEYS: Record<string, string> = {
  target_word: "flashcards.settings.fields.targetWord",
  translation: "flashcards.settings.fields.translation",
  example_sentence_l2: "flashcards.settings.fields.exampleSentenceL2",
  example_sentence_l1: "flashcards.settings.fields.exampleSentenceL1",
  word_audio: "flashcards.settings.fields.wordAudio",
  sentence_audio: "flashcards.settings.fields.sentenceAudio",
  picture: "flashcards.settings.fields.picture",
  pronunciation_word: "flashcards.settings.fields.pronunciationWord",
  pronunciation_sentence: "flashcards.settings.fields.pronunciationSentence",
};

// i18n keys per server card-layout preset key (FlashcardCardType::PRESETS) —
// same pattern as FIELD_LABEL_KEYS: the server sends stable codes, never labels.
export const PRESET_LABEL_KEYS: Record<string, string> = {
  default: "flashcards.settings.presets.default",
  visual_recall: "flashcards.settings.presets.visualRecall",
  listening: "flashcards.settings.presets.listening",
};

export type FlashcardCardTypePreset = ResultOf<typeof CARD_TYPE_PRESETS_QUERY>["flashcardCardTypePresets"][number];

// Client-side copy of the server's Default layout (FlashcardCardType::PRESETS
// [:default]). Used ONLY when Query.flashcardCardTypePresets fails, so "Add
// card type" still adds the canonical layout instead of a bare word→translation
// row. Keep in sync with the model if the Default preset ever changes.
export const DEFAULT_CARD_TYPE_PRESET: FlashcardCardTypePreset = {
  key: "default",
  frontFields: ["target_word", "word_audio"],
  backFields: ["translation", "picture", "example_sentence_l2", "example_sentence_l1"],
};

// Languages whose cards carry a romanisation field: every non-Latin script in
// the catalog, plus Vietnamese (tone-marked Latin). Mirrors
// Ai::ClaudeClient::PRONUNCIATION_LANGUAGES;
// spec/frontend/pronunciation_languages_spec.rb pins the two equal.
export const PRONUNCIATION_LANGUAGES = new Set([
  "ar", "he", "fa", "ur", "ps", "ug", "sd", "ks", "yi", "prs", "ckb", "arz", "apc", "ary", "afb", "aeb", "arq",
  "ru", "uk", "bg", "sr", "mk", "be", "kk", "ky", "tg", "mn", "tt", "ba", "cv", "ce", "av", "os", "kv", "ab", "cu",
  "el", "hy", "ka", "am", "ti",
  "hi", "mr", "ne", "sa", "bn", "as", "pa", "gu", "or", "ta", "te", "kn", "ml", "si", "dz", "bo", "bho", "mai", "sat", "mni", "doi", "kok", "syl",
  "th", "lo", "km", "my",
  "ja", "zh", "yue", "hak", "nan", "wuu", "gan", "hsn", "ko",
  "ii", "dv", "iu", "cr", "zgh", "vi",
]);
